import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import { Absence, MaternityCase } from "../models/absences";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import { AbsenceType } from "../models/absenceTypes-model";
import { getQueryClient, PERIOD_STALE_TIME, invalidatePeriod } from "../queryClient";

// An absence edit changes the absence list, the work-hour acceptance pages, and
// conflict calculations.
const invalidateAbsenceFamily = () =>
  invalidatePeriod("absences", "webAcceptPages", "conflicts");

class AbsencesService {
  async fetchAbsencesByEmployment(employmentId: number): Promise<Absence[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Absence[]>(configService.personaleOrAdminUrl("absences.json"), {
          params: {
            employment_id: employmentId
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Absence.fromJSONArray(ArrayFromEnvelope<Absence>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async fetchAbsences(fra: Date, tilOgMed: Date): Promise<Absence[]> {
    const utcFra = convertDateOnlyToUtc(fra);
    const utcTilOgMed = convertDateOnlyToUtc(tilOgMed);
    const institutionId = configService.institutionId();

    // "byRange" namespace so prefix-invalidation of "absences" also reaches
    // EmploymentTimeOverview's ["absences","byEmployment",...] queries.
    const raw = await getQueryClient().fetchQuery({
      queryKey: ["absences", "byRange", institutionId, utcFra, utcTilOgMed],
      queryFn: async () => {
        const response = await axios.get<Absence[]>(
          configService.personaleOrAdminUrl("absences.json"),
          {
            params: {
              institution_id: institutionId,
              to_date: utcTilOgMed,
              from_date: utcFra
            }
          }
        );
        return ArrayFromEnvelope<Absence>(response.data);
      },
      staleTime: PERIOD_STALE_TIME
    });
    return Absence.fromJSONArray(raw);
  }
  async fetchAbsenceById(id: number): Promise<Absence | undefined> {
    return new Promise((resolve, reject) => {
      axios
        .get<Absence>(configService.APIInstitutionUrl("absences/" + id + ".json"))
        .then((response: AxiosResponse) => {
          const absence: Absence = Absence.fromJSON(ObjectFromEnvelope<Absence>(response.data));
          resolve(absence);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async fetchAbsenceTypes(): Promise<AbsenceType[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<AbsenceType[]>(
          configService.personaleOrAdminUrl(
            "absence_types.json?institution_id=" + configService.institutionId()
          )
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceType.fromJSONArray(ArrayFromEnvelope<AbsenceType>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async createAbsence(absence: Absence): Promise<Absence> {
    return new Promise((resolve, reject) => {
      axios
        .post<Absence>(configService.APIInstitutionUrl("absences.json"), absence)
        .then((response: AxiosResponse) => {
          const parsed = Absence.fromJSON<Absence>(ObjectFromEnvelope<Absence>(response.data));
          invalidateAbsenceFamily();
          resolve(parsed);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async updateAbsence(absence: Absence): Promise<Absence> {
    return new Promise((resolve, reject) => {
      axios
        .put<Absence>(configService.APIInstitutionUrl("absences/" + absence.id + ".json"), absence)
        .then((response: AxiosResponse) => {
          const parsed = Absence.fromJSON<Absence>(ObjectFromEnvelope<Absence>(response.data));
          invalidateAbsenceFamily();
          resolve(parsed);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async deleteAbsence(absenceId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Absence>(configService.APIInstitutionUrl("absences/" + absenceId + ".json"))
        .then((response: AxiosResponse) => {
          invalidateAbsenceFamily();
          resolve(response.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async fileAbsence(absenceIds: number[]): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .post<Absence>(configService.APIInstitutionUrl("absences/file_absences.json"), {
          absences: absenceIds.toString()
        })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async getMaternityCases(employeeId: number): Promise<MaternityCase[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<any>(
          configService.APIInstitutionUrl(
            "absences/get_maternity_cases.json?employee_id=" + employeeId
          )
        )
        .then((response: AxiosResponse) => {
          resolve(MaternityCase.fromJSONArray(ArrayFromEnvelope<MaternityCase>(response.data)));
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async getChildren(employeeId: number): Promise<any[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<any>(
          configService.APIInstitutionUrl("absences/get_children.json?employee_id=" + employeeId)
        )
        .then((response: AxiosResponse) => {
          resolve(ArrayFromEnvelope<MaternityCase>(response.data));
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }

  async createAbsenceType(absenceType: AbsenceType): Promise<AbsenceType> {
    return new Promise((resolve, reject) => {
      axios
        .post<AbsenceType>(
          configService.APIInstitutionUrl("absence_types.json"),
          absenceType.convertToPostObject()
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceType.fromJSON(ObjectFromEnvelope<AbsenceType>(response.data)));
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async updateAbsenceType(absenceType: AbsenceType): Promise<AbsenceType> {
    return new Promise((resolve, reject) => {
      axios
        .put<AbsenceType>(
          configService.APIInstitutionUrl("absence_types/" + absenceType.id + ".json"),
          absenceType.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(AbsenceType.fromJSON(ObjectFromEnvelope<AbsenceType>(response.data)));
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async deleteAbsenceType(absenceTypeId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<AbsenceType>(
          configService.APIInstitutionUrl("absence_types/" + absenceTypeId + ".json")
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async approveAbsence(absenceId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .post<void>(configService.APIInstitutionUrl(`absences/${absenceId}/approve.json`))
        .then(() => {
          resolve();
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
  async rejectAbsence(absenceId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .post<void>(configService.APIInstitutionUrl(`absences/${absenceId}/reject.json`))
        .then(() => {
          resolve();
        })
        .catch((error) => {
          return reject(error);
        });
    });
  }
}

export const absencesService: AbsencesService = new AbsencesService();
